import { useEffect, useState } from "react";
import { Plus, Upload, Eye, Check } from "lucide-react";
import { api, formatApiError, formatTL, formatDate } from "@/lib/api";
import { DEED_STEPS, FIN_TYPES, openBlob } from "@/lib/docOptions";
import { DeedForm } from "@/components/docs/DeedForm";
import { DocRow } from "@/components/docs/DocRow";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const Steps = ({ d, onChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={`deed-steps-${d.id}`}>
    {Object.entries(DEED_STEPS).map(([k, l], i) => {
      const done = d.steps?.[k];
      return (
        <button key={k} data-testid={`deed-step-${k}-${d.id}`} onClick={() => onChange(k, !done)}
          className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${done ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "hover:border-slate-400 text-slate-600"}`}>
          <div className="flex items-center gap-1.5 font-semibold">{done ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 text-center">{i + 1}</span>}{l}</div>
          <div className="text-[10px] mt-0.5">{done ? formatDate(done) : "Bekliyor"}</div>
        </button>
      );
    })}
  </div>
);

const FileSlot = ({ d, kind, label, onChanged }) => {
  const f = d[`${kind}_file`];
  const upload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/deeds/${d.id}/files/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${label} yüklendi`); onChanged(); }
    catch (x) { toast.error(formatApiError(x.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="font-medium">{label}:</span>
      {f ? <button data-testid={`deed-file-view-${kind}-${d.id}`} className="text-emerald-700 underline flex items-center gap-1" onClick={() => openBlob(`/deeds/${d.id}/files/${kind}`)}><Eye className="w-3 h-3" />{f.filename}</button> : <span className="text-amber-700">Yüklenmedi</span>}
      <label className="cursor-pointer text-slate-600 hover:text-slate-900 flex items-center gap-1"><Upload className="w-3 h-3" />{f ? "Değiştir" : "Yükle"}
        <input data-testid={`deed-file-input-${kind}-${d.id}`} type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} /></label>
    </div>
  );
};

export default function DeedsPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/deeds").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const step = async (d, s, done) => {
    try { await api.put(`/deeds/${d.id}/step`, { step: s, done }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sub = (d) => `${d.registry_office || "Tapu müdürlüğü -"} · Randevu: ${d.appointment_at ? new Date(d.appointment_at).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }) : "-"} · ${formatTL(d.sale_price)} · ${FIN_TYPES[d.financing]}${d.financing_institution ? ` (${d.financing_institution})` : ""}`;

  return (
    <div className="space-y-6" data-testid="deeds-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Tapu Takip</h1>
          <p className="text-slate-500 text-sm mt-1">Tapu işlemlerini adım adım takip edin; harç ve döner sermaye otomatik hesaplanır.</p>
        </div>
        <Button data-testid="new-deeds-btn" className="bg-slate-900" onClick={() => setForm({})}><Plus className="w-4 h-4 mr-1" />Yeni tapu takibi</Button>
      </div>
      {items.length === 0 ? <Card className="p-10 text-center text-sm text-slate-500">Henüz tapu takibi yok</Card> : items.map((d) => (
        <DocRow key={d.id} kind="deeds" d={d} subtitle={sub(d)} onEdit={(x) => setForm({ editing: x })} onChanged={load}>
          <div className="text-xs text-slate-600">Harç {formatTL(d.harc)} · Döner sermaye {formatTL(d.doner_sermaye)} · Satıcı {d.seller?.name || "-"} → Alıcı {d.buyer?.name || "-"}</div>
          <Steps d={d} onChange={(s, done) => step(d, s, done)} />
          {(d.is_proxy || d.has_mortgage) && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 pt-1">
              {d.is_proxy && <FileSlot d={d} kind="proxy" label={`Vekalet (${d.proxy_notary || "-"} · Yev. ${d.proxy_yevmiye || "-"})`} onChanged={load} />}
              {d.has_mortgage && <FileSlot d={d} kind="mortgage" label="İpotek evrakı" onChanged={load} />}
            </div>
          )}
        </DocRow>
      ))}
      <DeedForm open={!!form} editing={form?.editing} onClose={() => setForm(null)} onSaved={load} />
    </div>
  );
}
